import { useEffect } from "react";

// Fades sections in as they scroll into view, once each. Mark an element with data-reveal;
// this sets data-revealed on it when it enters the viewport (see "MOTION" in index.css).
// Content is only hidden once this has started, and never for visitors who prefer reduced motion.
export default function RevealManager() {
  useEffect(() => {
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduceMotion || !("IntersectionObserver" in window)) return;

    const root = document.documentElement;
    root.classList.add("km-reveal-ready");

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        (entry.target as HTMLElement).dataset.revealed = "true";
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.12, rootMargin: "0px 0px -40px 0px" });

    // Pick up elements on the first page and on every page rendered after navigation.
    // Tracked per run (not as a DOM flag) so a remount re-observes anything not yet revealed.
    const watched = new WeakSet<Element>();
    const watch = () => {
      document.querySelectorAll<HTMLElement>("[data-reveal]:not([data-revealed])").forEach((el) => {
        if (watched.has(el)) return;
        watched.add(el);
        observer.observe(el);
      });
    };

    watch();
    const mutations = new MutationObserver(watch);
    mutations.observe(document.body, { childList: true, subtree: true });

    return () => {
      mutations.disconnect();
      observer.disconnect();
      root.classList.remove("km-reveal-ready");
    };
  }, []);

  return null;
}
